import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { statuses } from "../../constants/statuses";

export interface Filters {
  creationTimeFrom?: string;
  creationTimeTo?: string;
  statuses: string[];
  paymentId?: string;
  merchantId?: string;
  amountFrom?: number;
  amountTo?: number;
}

interface HistoryFilterProps {
  onClose: () => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
}

export const HistoryFilter: React.FC<HistoryFilterProps> = ({
  onClose,
  filters,
  setFilters,
}) => {
  const [localFilters, setLocalFilters] = useState<Filters>(filters);
  const [show, setShow] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setShow(true);
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleApply = () => {
    setFilters(localFilters);
    handleClose();
  };

  const handleClear = () => {
    const cleared: Filters = {
      creationTimeFrom: undefined,
      creationTimeTo: undefined,
      statuses: [],
      paymentId: undefined,
      merchantId: undefined,
      amountFrom: undefined,
      amountTo: undefined,
    };
    setLocalFilters(cleared);
    setFilters(cleared);
  };

  const handleClose = () => {
    setShow(false);
    timerRef.current = setTimeout(onClose, 300);
  };

  const toggleStatus = (status: string) => {
    setLocalFilters((prev) => {
      const exists = prev.statuses.includes(status);
      return {
        ...prev,
        statuses: exists
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      };
    });
  };

  const activeFilterCount = Object.entries(localFilters).reduce(
    (acc, [key, value]) => {
      if (key === "statuses" && Array.isArray(value))
        return acc + (value.length > 0 ? 1 : 0);
      if (value !== undefined && value !== "") return acc + 1;
      return acc;
    },
    0
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-center items-center">
      <div
        className={`w-full sm:max-w-md sm:rounded-lg bg-white dark:bg-[#1f1f1f] p-6 max-h-[85vh] overflow-y-auto shadow-lg border border-gray-700
        transform transition-all duration-300 ease-out
        ${
          show
            ? "opacity-100 translate-y-0 sm:scale-100"
            : "opacity-0 translate-y-full sm:scale-95"
        }
        `}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-black dark:text-white">
            Filters ({activeFilterCount})
          </h2>
          <button onClick={handleClose}>
            <X
              className="text-gray-500 hover:text-black dark:hover:text-white"
              size={20}
            />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-300">
              Range from - to
            </label>
            <input
              type="date"
              value={localFilters.creationTimeFrom || ""}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  creationTimeFrom: e.target.value,
                })
              }
              className="mt-1 w-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded px-3 py-2"
            />
            <input
              type="date"
              value={localFilters.creationTimeTo || ""}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  creationTimeTo: e.target.value,
                })
              }
              className="mt-2 w-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 dark:text-gray-300">
              Status
            </label>
            <div className="flex flex-wrap gap-2 mt-1">
              {statuses.map((s) => (
                <span
                  key={s.value}
                  onClick={() => toggleStatus(s.value)}
                  className={`px-2 py-1 text-xs rounded-full cursor-pointer select-none ${
                    localFilters.statuses.includes(s.value)
                      ? `${s.color} text-white`
                      : "bg-gray-300 dark:bg-gray-700 text-black dark:text-white"
                  }`}
                >
                  {s.label}
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-500 dark:text-gray-300">
              Payment ID
            </label>
            <input
              type="text"
              value={localFilters.paymentId || ""}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, paymentId: e.target.value })
              }
              className="mt-1 w-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 dark:text-gray-300">
              Merchant ID
            </label>
            <input
              type="text"
              value={localFilters.merchantId || ""}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, merchantId: e.target.value })
              }
              className="mt-1 w-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 dark:text-gray-300">
              Amount
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={localFilters.amountFrom || ""}
                placeholder="From"
                min="0"
                onChange={(e) =>
                  setLocalFilters({
                    ...localFilters,
                    amountFrom: parseFloat(e.target.value),
                  })
                }
                className="mt-1 w-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded px-3 py-2"
              />
              <input
                type="number"
                value={localFilters.amountTo || ""}
                placeholder="To"
                onChange={(e) =>
                  setLocalFilters({
                    ...localFilters,
                    amountTo: parseFloat(e.target.value),
                  })
                }
                className="mt-1 w-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded px-3 py-2"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button
              onClick={handleClear}
              className="px-4 py-2 rounded bg-gray-300 dark:bg-gray-700 text-black dark:text-white"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
